import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { clearTradeToken, getJson, saveTradeToken, tradeToken } from '../utils/shopApi';
import useCart from '../utils/shopCart';
import { AddToCart, CartView, CheckoutView, InvoiceView, OrderView } from './ShopOrdering';
import ReportProductModal from './ReportProductModal';
import './shop.css';

const Photo = ({ photo, alt, size, ...rest }) => (photo
  ? <img src={size === 'full' ? photo.full : photo.thumb} alt={alt} loading="lazy" {...rest} />
  : <div className="d-flex align-items-center justify-content-center bg-body-secondary text-muted" style={{ aspectRatio: '1 / 1', ...rest.style }}><i className="bi bi-image fs-1" /></div>);

function videoEmbed(url) {
  const yt = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/.exec(url || '');
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = /vimeo\.com\/(\d+)/.exec(url || '');
  return vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
}

const SEARCH_MIN_PRODUCTS = 8; // a store with fewer products than this needs no search box

/** The search box in the header: on every page, taking the buyer to the product list with the results. On a phone it is a magnifier that opens a bar. */
function HeaderSearch({ home, open, onToggle, onDone }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [text, setText] = useState(params.get('q') || '');
  useEffect(() => { setText(params.get('q') || ''); }, [params]);
  const submit = (e) => {
    e.preventDefault();
    const q = text.trim();
    navigate(q ? `${home}?q=${encodeURIComponent(q)}` : home);
    if (onDone) onDone();
  };
  const field = (
    <Form className="shop-search" onSubmit={submit} role="search">
      <InputGroup>
        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
        <Form.Control type="search" placeholder="Search products" aria-label="Search products" value={text} onChange={(e) => setText(e.target.value)} data-testid="shop-search" />
      </InputGroup>
    </Form>
  );
  return (
    <>
      <div className="shop-header-search d-none d-md-block flex-grow-1">{field}</div>
      <Button variant="outline-secondary" size="sm" className="shop-icon-btn d-md-none ms-auto" aria-label="Search products" aria-expanded={open} onClick={onToggle}><i className={`bi ${open ? 'bi-x-lg' : 'bi-search'}`} /></Button>
      {open && <div className="shop-search-bar d-md-none" data-testid="shop-search-bar">{field}</div>}
    </>
  );
}

/** What a buyer sees at /shop/<store>: the products, search and categories, a product page, and the store's own details and policies. */
function ShopPage() {
  const { slug, page, id } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [trade, setTrade] = useState(null); // the business customer a private link was opened for
  const [tradeNotice, setTradeNotice] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false); // the phone's search bar
  const [logoFailed, setLogoFailed] = useState(false); // a logo that cannot be loaded gives way to the store's name
  const base = `/api/public/shop/${encodeURIComponent(slug)}`;
  const cart = useCart(slug);

  // A trade link opens the store with ?t=<token>: keep the token in this browser, take it out of the address, and ask whose it is
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromLink = params.get('t');
    if (fromLink) {
      saveTradeToken(slug, fromLink);
      params.delete('t');
      window.history.replaceState(null, '', window.location.pathname + (params.toString() ? `?${params}` : ''));
    }
    if (!tradeToken(slug)) return;
    getJson(`${base}/trade`).then(setTrade).catch((e) => { clearTradeToken(slug); setTrade(null); setTradeNotice(e.message); });
  }, [slug, base]);

  const leaveTrade = () => {
    clearTradeToken(slug);
    window.location.assign(`/shop/${slug}`);
  };

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });
    return (v) => f.format(Number(v || 0));
  }, []);

  useEffect(() => {
    document.title = 'Store';
    getJson(base).then((d) => { setInfo(d); document.title = d.name; }).catch((e) => setError(e.message));
  }, [base]);

  if (error) return <Container className="py-5" style={{ maxWidth: 640 }}><Alert variant="warning">{error}</Alert></Container>;
  if (!info) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;

  const home = `/shop/${slug}`;
  const seller = info.seller || {};
  const hasSearch = Number(info.productCount || 0) >= SEARCH_MIN_PRODUCTS || Boolean(new URLSearchParams(window.location.search).get('q'));
  const grievance = seller.grievance;
  return (
    <div className="shop" data-testid="shop">
      <header className="shop-header">
        <Container className="d-flex align-items-center flex-wrap gap-3 py-2">
          <Link to={home} className="shop-brand">
            {seller.logoUrl && !logoFailed
              ? <img src={seller.logoUrl} alt={info.name} data-testid="shop-logo" onError={() => setLogoFailed(true)} />
              : <span className="shop-name" data-testid="shop-name">{info.name}</span>}
          </Link>
          {hasSearch && <HeaderSearch home={home} open={searchOpen} onToggle={() => setSearchOpen((o) => !o)} onDone={() => setSearchOpen(false)} />}
          <nav className="shop-nav ms-auto d-flex align-items-center gap-1">
            <Link to={`${home}/about`} className={`shop-link d-none d-sm-inline ${page === 'about' ? 'active' : ''}`}>About</Link>
            <Link to={`${home}/policies`} className={`shop-link d-none d-sm-inline ${page === 'policies' ? 'active' : ''}`}>Shipping &amp; returns</Link>
            <Link to={`${home}/cart`} className="btn btn-outline-primary btn-sm shop-cart-btn ms-1" data-testid="cart-link">
              <i className="bi bi-bag" /> Cart{cart.count > 0 && <Badge bg="primary" pill>{cart.count}</Badge>}
            </Link>
          </nav>
        </Container>
        {trade && (
          <div className="bg-body-secondary border-top py-2 small" data-testid="trade-banner">
            <Container className="d-flex align-items-center flex-wrap gap-2">
              <i className="bi bi-person-check" />
              <span>Trade prices for <strong>{trade.name}</strong>{trade.listName ? ` (${trade.listName})` : ''}.</span>
              <Button variant="link" size="sm" className="p-0 align-baseline" onClick={leaveTrade}>Not you? Leave trade prices</Button>
            </Container>
          </div>
        )}
      </header>
      <main className="shop-main">
        <Container className="py-4">
          {tradeNotice && <Alert variant="warning" dismissible onClose={() => setTradeNotice(null)} data-testid="trade-notice">{tradeNotice}</Alert>}
          {page === 'product' && id ? <ProductView base={base} home={home} id={id} info={info} money={money} cart={cart} />
            : page === 'cart' ? <CartView base={base} home={home} cart={cart} money={money} />
              : page === 'checkout' ? <CheckoutView base={base} home={home} cart={cart} info={info} money={money} trade={trade} />
                : page === 'order' && id ? <OrderView base={base} home={home} token={id} money={money} />
                  : page === 'invoice' && id ? <InvoiceView base={base} home={home} token={id} />
                    : page === 'about' ? <AboutView info={info} />
                      : page === 'policies' ? <PoliciesView info={info} />
                        : <ProductList base={base} home={home} money={money} cart={cart} info={info} />}
        </Container>
      </main>
      <footer className="shop-footer">
        <Container className="py-4">
          <Row className="g-4 small">
            <Col md={5}>
              <div className="fw-semibold mb-1">{seller.name}</div>
              {seller.address && <div className="text-muted">{seller.address}</div>}
              {seller.gstin && <div className="text-muted">GSTIN {seller.gstin}</div>}
            </Col>
            <Col md={4}>
              <div className="fw-semibold mb-1">Contact</div>
              {info.contactPhone && <div><i className="bi bi-telephone me-1 text-muted" /><a href={`tel:${info.contactPhone.replace(/[^+\d]/g, '')}`}>{info.contactPhone}</a></div>}
              {info.contactEmail && <div><i className="bi bi-envelope me-1 text-muted" /><a href={`mailto:${info.contactEmail}`}>{info.contactEmail}</a></div>}
              {grievance && (
                <div className="mt-2 text-muted" data-testid="shop-grievance">
                  Grievance officer: {grievance.name}{grievance.email ? ` · ${grievance.email}` : ''}{grievance.phone ? ` · ${grievance.phone}` : ''}
                </div>
              )}
            </Col>
            <Col md={3}>
              <div className="fw-semibold mb-1">Help</div>
              <div><Link to={`${home}/about`}>About us</Link></div>
              <div><Link to={`${home}/policies`}>Shipping &amp; returns</Link></div>
              <div><Link to={`${home}/cart`}>Your cart</Link></div>
              <div className="shop-credit mt-3 d-flex align-items-center gap-1 text-muted" data-testid="shop-credit">
                <img src="/favicon.svg" alt="" width="14" height="14" />
                <span>Online store by</span>
                <a href="https://ibigdo.com" target="_blank" rel="noopener noreferrer" className="fw-semibold">iBigDo</a>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
}

function ProductList({ base, home, money, cart, info }) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const category = params.get('category') || '';
  const pageNo = Number(params.get('page') || 0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const qs = new URLSearchParams({ page: String(pageNo) });
    if (q) qs.set('q', q);
    if (category) qs.set('category', category);
    getJson(`${base}/products?${qs}`).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message));
  }, [base, q, category, pageNo]);

  const go = useCallback((next) => {
    const merged = { q, category, page: String(pageNo), ...next };
    const clean = Object.fromEntries(Object.entries(merged).filter(([k, v]) => v && !(k === 'page' && v === '0')));
    setParams(clean);
  }, [q, category, pageNo, setParams]);

  if (error) return <Alert variant="warning">{error}</Alert>;
  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const intro = info?.about ? (info.about.length > 150 ? `${info.about.slice(0, 147).trim()}...` : info.about) : null;
  return (
    <>
      <div className="shop-hero mb-4">
        <h1>{info?.tagline || `Welcome to ${info?.name}`}</h1>
        {intro && <p className="text-muted mb-0">{intro}</p>}
        {q && <p className="mb-0 mt-2" data-testid="shop-search-note">Results for &ldquo;{q}&rdquo; <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => go({ q: '', page: '0' })}>Clear</Button></p>}
      </div>
      {data.categories.length > 1 && (
        <div className="d-flex flex-wrap gap-2 mb-4">
          <Button size="sm" className="shop-chip" variant={category ? 'outline-secondary' : 'primary'} onClick={() => go({ category: '', page: '0' })}>All</Button>
          {data.categories.map((c) => (
            <Button key={c.name} size="sm" className="shop-chip" variant={category.toLowerCase() === c.name.toLowerCase() ? 'primary' : 'outline-secondary'} onClick={() => go({ category: c.name, page: '0' })}>
              {c.name}<span className="count">{c.count}</span>
            </Button>
          ))}
        </div>
      )}
      {data.products.length === 0 ? (
        <div className="shop-card text-center text-muted py-5"><i className="bi bi-bag fs-1 d-block mb-2" />{q || category ? 'No products match your search.' : 'No products to show yet.'}</div>
      ) : (
        <Row className="g-3 g-md-4" data-testid="shop-products">
          {data.products.map((p) => (
            <Col key={p.id} xs={6} md={4} lg={3}>
              <Card className="product-card h-100">
                <Link to={`${home}/product/${p.id}`} className="photo" aria-label={p.name}>
                  <Photo photo={p.photos[0]} alt={p.name} />
                  {!p.inStock && <Badge bg="dark">Out of stock</Badge>}
                </Link>
                <Card.Body className="d-flex flex-column">
                  <Link to={`${home}/product/${p.id}`} className="name">{p.name}</Link>
                  <div className="mt-auto pt-2">
                    <div className="mb-2"><span className="shop-price">{money(p.price)}</span> {Number(p.gstRate) > 0 && <span className="small text-muted">+ {Number(p.gstRate)}% GST</span>}{p.tradePrice && <Badge bg="success" className="ms-1">Your price</Badge>}</div>
                    {p.inStock ? <AddToCart cart={cart} productId={p.id} inStock home={home} /> : <Button size="sm" variant="outline-secondary" className="w-100" disabled>Out of stock</Button>}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      {data.pages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button size="sm" variant="outline-secondary" className="shop-chip" disabled={data.page <= 0} onClick={() => go({ page: String(data.page - 1) })}><i className="bi bi-chevron-left" /> Previous</Button>
          <span className="small text-muted">Page {data.page + 1} of {data.pages}</span>
          <Button size="sm" variant="outline-secondary" className="shop-chip" disabled={data.page >= data.pages - 1} onClick={() => go({ page: String(data.page + 1) })}>Next <i className="bi bi-chevron-right" /></Button>
        </div>
      )}
    </>
  );
}

function ProductView({ base, home, id, money, info, cart }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(0);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    setShown(0);
    getJson(`${base}/products/${encodeURIComponent(id)}`).then(setP).catch((e) => setError(e.message));
  }, [base, id]);

  if (error) return <Alert variant="warning">{error} <Button variant="link" className="p-0" onClick={() => navigate(home)}>Back to the store</Button></Alert>;
  if (!p) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  const embed = videoEmbed(p.video);
  const tax = Number(p.gstRate) > 0;

  return (
    <div data-testid="shop-product">
      <Link to={home} className="back-link"><i className="bi bi-arrow-left" /> All products</Link>
      <Row className="g-4 mt-1">
        <Col md={6}>
          <div className="shop-card p-2">
            <Photo photo={p.photos[shown]} size="full" alt={p.name} className="img-fluid rounded-3 w-100" />
            {p.photos.length > 1 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {p.photos.map((ph, i) => (
                  <img key={ph.id} src={ph.thumb} alt="" width={64} height={64} loading="lazy" role="button" onClick={() => setShown(i)}
                    className={`rounded-3 ${i === shown ? 'border border-primary border-2' : 'border'}`} style={{ objectFit: 'cover' }} />
                ))}
              </div>
            )}
          </div>
          {embed && <div className="ratio ratio-16x9 mt-3 rounded-4 overflow-hidden"><iframe src={embed} title={`${p.name} video`} allowFullScreen loading="lazy" /></div>}
        </Col>
        <Col md={6}>
          <div className="shop-card p-4 sticky-summary">
            {p.brand && <div className="text-muted small text-uppercase">{p.brand}</div>}
            <h1 className="h3 mb-2">{p.name}</h1>
            <div className="d-flex align-items-baseline flex-wrap gap-2">
              <span className="shop-price fs-3">{money(p.price)}</span>{p.unit ? <span className="text-muted">/ {p.unit}</span> : null}{p.tradePrice && <Badge bg="success">Your price</Badge>}
            </div>
            <div className="small text-muted mb-3">{tax ? (info.pricesIncludeGst ? `Includes ${Number(p.gstRate)}% GST` : `Plus ${Number(p.gstRate)}% GST`) : 'No GST'}</div>
            <div className="mb-3">{p.inStock ? <Badge bg="success-subtle" text="success-emphasis" className="fw-semibold"><i className="bi bi-check-circle me-1" />In stock</Badge> : <Badge bg="warning-subtle" text="warning-emphasis" className="fw-semibold">Out of stock</Badge>}</div>
            {p.description && <p style={{ whiteSpace: 'pre-line' }}>{p.description}</p>}
            <AddToCart cart={cart} productId={p.id} inStock={p.inStock} home={home} withQuantity />
            <hr className="my-3" />
            <div className="small text-muted d-flex flex-wrap gap-3">
              {reporting && <ReportProductModal slug={info.slug} productId={p.id} productName={p.name} onClose={() => setReporting(false)} />}
              {p.sku && <span>Code {p.sku}</span>}
              <Link to={`${home}/policies`} className="back-link"><i className="bi bi-truck" /> Shipping &amp; returns</Link>
              <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setReporting(true)} data-testid="report-this-product"><i className="bi bi-flag me-1" />Report this product</Button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

const Text = ({ title, text }) => (text ? <section className="shop-card p-4 mb-3"><h2 className="h5">{title}</h2><p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{text}</p></section> : null);

function AboutView({ info }) {
  return <div style={{ maxWidth: 760 }}><Text title={`About ${info.name}`} text={info.about || 'Welcome to our store.'} /></div>;
}

function PoliciesView({ info }) {
  const shipping = info.shipping || {};
  const lines = [];
  if (shipping.byWeight) lines.push(`Shipping is charged by weight and where it is going${Number(shipping.freeOver) > 0 ? `, and is free on orders over ₹${Number(shipping.freeOver)}` : ''}. You see the exact charge once you choose your state at checkout.`);
  else if (Number(shipping.flat) > 0) lines.push(`Shipping: ₹${Number(shipping.flat)}${Number(shipping.freeOver) > 0 ? `, free on orders over ₹${Number(shipping.freeOver)}` : ''}.`);
  else lines.push('Shipping is free.');
  if (Number(info.minOrderAmount) > 0) lines.push(`Smallest order: ₹${Number(info.minOrderAmount)}.`);
  const pay = [info.payments?.online && 'card, UPI or net banking (paid online)', info.payments?.upi && 'UPI', info.payments?.bankTransfer && 'bank transfer', info.payments?.cod && 'cash on delivery'].filter(Boolean);
  if (pay.length) lines.push(`You can pay by ${pay.join(', ')}.`);
  return (
    <div style={{ maxWidth: 760 }}>
      <section className="shop-card p-4 mb-3"><h2 className="h5">Delivery and payment</h2><p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}{info.payments?.note ? `\n${info.payments.note}` : ''}</p></section>
      <Text title="Shipping and delivery" text={info.shippingPolicy} />
      <Text title="Returns and refunds" text={info.returnsPolicy} />
      <Text title="Terms of sale" text={info.terms} />
      {info.seller?.grievance && (
        <section className="shop-card p-4 mb-3" data-testid="policies-grievance"><h2 className="h5">Complaints</h2><p>If something is wrong with your order, write to our grievance officer, {info.seller.grievance.name}{info.seller.grievance.email ? `, at ${info.seller.grievance.email}` : ''}{info.seller.grievance.phone ? `${info.seller.grievance.email ? ' or' : ','} on ${info.seller.grievance.phone}` : ''}.</p></section>
      )}
    </div>
  );
}

export default ShopPage;
